import * as Yup from "yup";

import {
  Box,
  Checkbox,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  Typography,
  TextField as MUITextField,
  Tooltip,
  IconButton,
} from "@material-ui/core";
import { Form, Formik } from "formik";
import {
  CheckboxField,
  CustomPriceField,
  NumberField,
  PriceField,
  SelectField,
  SubmitButton,
  TextField,
} from "@microrealestate/commonui/components";
import React, { useCallback, useContext, useMemo, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import { StoreContext } from "../../store";
import useDialog from "../../hooks/useDialog";
import { useRouter } from "next/router";
import useTranslation from "next-translate/useTranslation";
import useFillStore from "../../hooks/useFillStore";
import { HelpOutline as HelpOutlineIcon } from "@material-ui/icons";
import { newRoomFormSteps } from "../shepherdStep/guideStep";

const initialValues = (properties, selectedProperty) => {
  const inValues = {
    totalRooms: 1,
    isBatch: false,
    name: "",
    propertyId: selectedProperty._id ? selectedProperty._id : properties.length > 0 ? properties[0].value : "",
    price: {
      daily: 0,
      weekly: 0,
      monthly: 0,
      yearly: 0,
    },
    note: "",
  };

  return inValues;
};

// async function fetchData(store) {
//   return await store.property.fetch()
// }

const useStyles = makeStyles((theme) => {
  const color = theme.palette.info.contrastText;
  return {
    chip_clean: {
      color,
      backgroundColor: ["#64B5F6", "!important"],
    },
    chip_dirty: {
      color,
      backgroundColor: ["#fc9300", "!important"],
    },
    chip_out_of_order: {
      color,
      backgroundColor: ["#eb0202", "!important"],
    },
  };
});

function NewRoomDialog({ open, setOpen, backPage, backPath }) {
  const classes = useStyles();

  const { t } = useTranslation("common");
  const store = useContext(StoreContext);
  const router = useRouter();
  const limitRoom =
    store.subscription.selected?.usage.activeRoom ||
    store.subscription.selected?.features.maxRoom ||
    store.subscription.selected?.maxRoom;
  const NewRoomAvailable = (limitRoom ?? Infinity) - store.room.items.length;

  // const [fetching] = useFillStore(fetchData)

  const [checkedFrequency, setCheckedFrequency] = useState({
    dailyFreq: false,
    weeklyFreq: false,
    monthlyFreq: true,
    yearlyFreq: false,
  });

  const statusIcons = {
    clean: "⭐",
    dirty: "🪣",
    out_of_order: "🛠️",
  };

  const roomStatus = [
    { id: "clean", value: "clean", label: t("Clean") },
    { id: "dirty", value: "dirty", label: t("Dirty") },
    { id: "out_of_order", value: "out_of_order", label: t("Out of Order") },
  ];

  const availableProperties = useMemo(() => {
    return store.property.items.map(({ _id, name }) => ({
      id: _id,
      value: _id,
      label: name,
    }));
  }, [store.property.items]);

  const handleFrequencyChange = (event) => {
    const { name, checked } = event.target;
    setCheckedFrequency({
      ...checkedFrequency,
      [name]: checked,
    });
  };

  const onStartTour = useCallback(() => {
    const tourSteps = newRoomFormSteps(store, router, t);
    store.guideTour.setupTour(tourSteps);
    store.guideTour.startTour();
  }, [store, router, t]);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const _onSubmit = useCallback(
    async (roomPart, { setSubmitting }) => {
      const { isBatch, ...updatedRoomPart } = roomPart;
      const { totalRooms, name } = updatedRoomPart;
      let rooms = { rooms: [] };
      if (isBatch) {
        for (let i = 1; i <= totalRooms; i++) {
          let room = {
            ...updatedRoomPart,
            name: `${name} ${i}`,
          };

          rooms.rooms.push(room);
        }

        const { status, data, error_code } = await store.room.createMany(rooms);

        if (status !== 200) {
          if (error_code) {
            switch (error_code) {
              case "MAX_ROOMS_REACHED":
                return store.pushToastMessage({
                  message: t("You have reached the maximum number of rooms for your current plan."),
                  severity: "error",
                });
              case "CAN_NOT_ADD_ROOMS":
                return store.pushToastMessage({
                  message: t("Cannot add room. Please contact the Superkos team via WhatsApp."),
                  severity: "error",
                });
              default:
                break;
            }
          }

          switch (status) {
            // Handle error messages
            case 422:
              return store.pushToastMessage({
                message: t("Property name is missing"),
                severity: "error",
              });
            case 403:
              return store.pushToastMessage({
                message: t("You are not allowed to add a property"),
                severity: "error",
              });
            case 409:
              return store.pushToastMessage({
                message: t("The property already exists"),
                severity: "error",
              });
            default:
              return store.pushToastMessage({
                message: t("Something went wrong"),
                severity: "error",
              });
          }
        } else {
          store.pushToastMessage({
            message: t("Success create new rooms"),
            severity: "success",
          });
        }

        // Use router.push to navigate without a full page reload
        await router.push(`/rooms/`);
      } else {
        const { status, data, error_code } = await store.room.create(roomPart);

        if (status !== 200) {
          if (error_code) {
            switch (error_code) {
              case "MAX_ROOMS_REACHED":
                return store.pushToastMessage({
                  message: t("You have reached the maximum number of rooms for your current plan."),
                  severity: "error",
                });
              case "CAN_NOT_ADD_ROOMS":
                return store.pushToastMessage({
                  message: t("Cannot add room. Please contact the Superkos team via WhatsApp."),
                  severity: "error",
                });
              default:
                break;
            }
          }

          switch (status) {
            // Handle error messages
            case 422:
              return store.pushToastMessage({
                message: t("Property name is missing"),
                severity: "error",
              });
            case 403:
              return store.pushToastMessage({
                message: t("You are not allowed to add a property"),
                severity: "error",
              });
            case 409:
              return store.pushToastMessage({
                message: t("The property already exists"),
                severity: "error",
              });
            default:
              return store.pushToastMessage({
                message: t("Something went wrong"),
                severity: "error",
              });
          }
        } else {
          store.pushToastMessage({
            message: t("Success create new room"),
            severity: "success",
          });
        }
        store.room.setSelected(data);

        // Use router.push to navigate without a full page reload
        await router.push(`/rooms/${data._id}/${encodeURI(backPage)}/${encodeURIComponent(backPath)}`);
      }

      handleClose();

      setSubmitting(false); // Set submitting to false after successful submission
    },
    [store, handleClose, router, backPage, backPath, t],
  );

  const roomStatusWithChip = useMemo(
    () =>
      roomStatus.map((room) => ({
        ...room,
        label: (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Chip
              size="small"
              label={
                <Typography variant="caption.fontSize" style={{ display: "flex", alignItems: "center" }}>
                  <span style={{ fontSize: 16 }}>{statusIcons[room.value]}</span>
                </Typography>
              }
              className={classes["chip_" + room.value]}
            />
            <span>{room.label}</span>
          </div>
        ),
      })),
    [roomStatus],
  );

  return (
    <Dialog
      maxWidth="sm"
      fullWidth
      open={open}
      onClose={handleClose}
      aria-labelledby="new-room-dialog"
      PaperProps={{
        className: "shepherd-newRoomDialog",
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box className="shepherd-newRoomDialog-title">{t("Add a new room")}</Box>
          <Tooltip title={t("Guide")}>
            <IconButton aria-label="guide" color="default" onClick={onStartTour}>
              <HelpOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogTitle>
      <Box p={1}>
        <Formik
          initialValues={initialValues(availableProperties, store.property.selected)}
          validationSchema={Yup.object().shape({
            isBatch: Yup.boolean(),
            totalRooms: Yup.mixed().when("isBatch", {
              is: true,
              then: Yup.number()
                .min(1, t("Total rooms must be at least 1"))
                .max(NewRoomAvailable, t("Cannot exceed maximum rooms"))
                .required(t("Total rooms is a required field")),
            }),
            name: Yup.string().required(t("Room name is a required field")),
            price: Yup.object().shape({
              daily: Yup.number().required(t("Price daily is a required field")),
              weekly: Yup.number().required(t("Price weekly is a required field")),
              monthly: Yup.number().required(t("Price monthly is a required field")),
              yearly: Yup.number().required(t("Price yearly is a required field")),
            }),

            // Adjust validation schema based on your form requirements
          })}
          onSubmit={_onSubmit}
        >
          {({ values, isSubmitting, setFieldValue }) => {
            return (
              <Form autoComplete="off">
                <DialogContent>
                  <Grid container spacing={1}>
                    <Grid item xs={12} md={!values.isBatch ? 12 : 8} className="shepherd-newRoomDialog-nameField">
                      <TextField label={t("Room Name") + " *"} name="name" />
                    </Grid>
                    {values.isBatch && (
                      <Grid item xs={12} md={4} className="shepherd-newRoomDialog-bulkAmountField">
                        <NumberField label={t("Total Rooms")} name="totalRooms" />
                      </Grid>
                    )}
                    <Grid item xs={12} className="shepherd-newRoomDialog-bulkCheckbox">
                      <CheckboxField name="isBatch" label={t("Add at once")} aria-label={t("Add at once")} />
                    </Grid>
                    {/* {!fetching && ( */}
                    <Grid item xs={12} className="shepherd-newRoomDialog-propertySelect">
                      <SelectField label={t("Property")} name="propertyId" values={availableProperties} />
                    </Grid>
                    {/* )} */}
                    <Grid item xs={12} className="shepherd-newRoomDialog-propertySelect">
                      <SelectField label={t("Room Status") + "*"} name="room_status" values={roomStatusWithChip} />
                    </Grid>
                    <Grid item xs={12} className="shepherd-newRoomDialog-noteField">
                      <TextField label={t("Note")} name="note" />
                    </Grid>
                    <Grid item container xs={12} className="shepherd-newRoomDialog-pricesField">
                      <Grid item xs={12} sm={6} className="shepherd-newRoomDialog-pricesField">
                        <Box display="flex" flexDirection="row">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={checkedFrequency.dailyFreq}
                                onChange={handleFrequencyChange}
                                name="dailyFreq"
                              />
                            }
                          />
                          <CustomPriceField
                            label={t("Price / 1 day") + " *"}
                            name="price.daily"
                            disabled={!checkedFrequency.dailyFreq}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} className="shepherd-newRoomDialog-pricesField">
                        <Box display="flex" flexDirection="row">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={checkedFrequency.weeklyFreq}
                                onChange={handleFrequencyChange}
                                name="weeklyFreq"
                              />
                            }
                          />
                          <CustomPriceField
                            label={t("Price / 1 week") + " *"}
                            name="price.weekly"
                            disabled={!checkedFrequency.weeklyFreq}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" flexDirection="row">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={checkedFrequency.monthlyFreq}
                                onChange={handleFrequencyChange}
                                name="monthlyFreq"
                                disabled
                              />
                            }
                          />
                          <CustomPriceField
                            label={t("Price / 1 month") + " *"}
                            name="price.monthly"
                            disabled={!checkedFrequency.monthlyFreq}
                          />
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box display="flex" flexDirection="row">
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={checkedFrequency.yearlyFreq}
                                onChange={handleFrequencyChange}
                                name="yearlyFreq"
                              />
                            }
                          />
                          <CustomPriceField
                            label={t("Price / 1 year") + " *"}
                            name="price.yearly"
                            disabled={!checkedFrequency.yearlyFreq}
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </Grid>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} color="primary" className="shepherd-cancelButton">
                    {t("Cancel")}
                  </Button>
                  <SubmitButton label={!isSubmitting ? t("Add") : t("Adding room")} className="shepherd-submitButton" />
                </DialogActions>
              </Form>
            );
          }}
        </Formik>
      </Box>
    </Dialog>
  );
}

export default function useNewRoomDialog() {
  return useDialog(NewRoomDialog);
}
